"use client"

import { useMemo, useState } from "react"
import { ChevronRight, ExternalLink, MoreHorizontal, SquarePen, X } from "lucide-react"

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { TonePill } from "@/components/graphite/surface"
import { TINT, stageHex, stageTint } from "@/lib/stage-colors"
import { cn } from "@/lib/utils"
import type { OverlayTab } from "../candidate-profile-sheet"

/**
 * The candidate record, built from canvas artboards 6a (desktop) and 8i/8j
 * (narrow). Written against the artboards rather than restyled out of the old
 * sheet: that sheet is a light-theme stack of collapsible cards with icon tiles
 * and coloured left borders, and no amount of token-swapping turns it into this.
 *
 * The shape of the thing, in the canvas's terms: identity and the three actions
 * you take on a person, then tabs, then the record itself — a short read, the
 * four numbers that decide a call, and the history. Working state (tasks,
 * comments, files, buckets) sits in a rail beside it on desktop and collapses to
 * a counts strip under the tabs when narrow.
 *
 * Everything responds to the container, not the viewport, because this overlay
 * lives inside a resizable panel.
 */

export interface ProfileExperience {
  title?: string
  company?: string
  starts?: string
  ends?: string
  description?: string
}

export interface ProfileComment {
  id: string
  /** Plain-text preview; the stored body is rich text. */
  preview: string
  author?: string
  label?: string
  when?: string
}

/** A school, as the record used to list them. */
export interface ProfileEducation {
  school: string
  detail?: string
  years?: string
}

export interface ProfileTask {
  id: string
  title: string
  due?: string
  status?: string
  overdue?: boolean
}

export interface GraphiteCandidateProfileData {
  name: string
  initials: string
  avatarUrl?: string
  /** "Staff Engineer, Platform · Klarna · Stockholm" */
  subtitle: string
  /** Current pipeline stage, when the record was opened from a job. */
  stage?: string
  stageColor?: string
  /** The job this candidate is in, when job-scoped. */
  jobTitle?: string
  summary?: string
  location?: string
  experienceYears?: string
  salary?: string
  bonus?: string
  /** The first few, as the record shows them before you ask for the rest. */
  experiences: ProfileExperience[]
  /** Every one of them, for when you do. */
  allExperiences: ProfileExperience[]
  totalExperiences: number
  skills: string[]
  /** Organisation tags. Distinct from skills: these are what your team decided. */
  tags: string[]
  education: ProfileEducation[]
  linkedinUrl?: string
  tasks: ProfileTask[]
  /** Undefined until the request behind it has landed — never a stand-in zero. */
  taskCount?: number
  comments: ProfileComment[]
  commentCount?: number
  /**
   * Undefined where the count is genuinely unknown, which today is always: there
   * is no list-files endpoint at all, and `GET /contacts/{id}/buckets` is keyed
   * by a contact id a candidate does not carry. A row is dropped rather than
   * shown as `0`, because a confident zero is a claim, and it is the one claim
   * the data cannot support.
   */
  fileCount?: number
  bucketCount?: number
}

const TABS: { id: OverlayTab; label: string }[] = [
  { id: "none", label: "Profile" },
  { id: "resume", label: "Résumé" },
  { id: "timeline", label: "Timeline" },
  { id: "events", label: "Events" },
  { id: "outreach", label: "Outreach" },
  { id: "placements", label: "Placements" },
]

const RAIL_LABEL = "text-[11.5px] font-medium text-muted-foreground"


/**
 * The record's overflow, which shipped with no handler at all.
 *
 * Before the rebuild these were two buttons in a "Quick Actions" card: put the
 * candidate on another job, or into a bucket. Both wrote for real, so both come
 * back — as one menu, because the record has no room for a card of buttons.
 */
export type CandidateActions = {
  jobs: { id: string; label: string }[]
  buckets: { id: string; label: string }[]
  canBucket: boolean
  isBusy: boolean
  putOnJob: (jobId: string, label: string) => void
  putInBucket: (bucketId: string, label: string) => void
}

/*
  14a and its light twin 14h draw the record's actions as four quiet words at
  the same weight — no filled pill among them. Move Stage had been a bordered
  teal button, which on white read as the only real control up there and made
  the other three look like captions.
*/
const HEADER_ACTION =
  "inline-flex h-10 items-center whitespace-nowrap rounded-md px-1 text-[12.5px] text-muted-foreground transition-colors hover:text-foreground"

function CandidateActionsMenu({
  actions,
  label,
}: {
  actions?: CandidateActions
  /**
   * 14a writes the wide header as four words — Add to Job, Add a task, Move
   * Stage, Edit — so on wide this menu is one of those words. Narrow has no
   * room for a row of them and keeps the overflow glyph.
   */
  label?: string
}) {
  const [open, setOpen] = useState(false)

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        {label ? (
          <button type="button" className={HEADER_ACTION}>
            {label}
          </button>
        ) : (
          <button
            type="button"
            aria-label="More actions"
            className="flex h-11 w-11 items-center justify-center text-muted-foreground"
          >
            <MoreHorizontal className="h-[18px] w-[18px]" />
          </button>
        )}
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel className={cn(RAIL_LABEL, "font-normal")}>
          Add to a job
        </DropdownMenuLabel>
        {actions?.jobs.length ? (
          actions.jobs.slice(0, 8).map((job) => (
            <DropdownMenuItem
              key={job.id}
              disabled={actions.isBusy}
              onSelect={() => actions.putOnJob(job.id, job.label)}
              className="text-[12.5px]"
            >
              <span className="truncate">{job.label}</span>
            </DropdownMenuItem>
          ))
        ) : (
          <DropdownMenuItem disabled className="text-[12.5px]">
            No jobs to add to
          </DropdownMenuItem>
        )}

        <DropdownMenuSeparator />
        <DropdownMenuLabel className={cn(RAIL_LABEL, "font-normal")}>
          Add to a bucket
        </DropdownMenuLabel>
        {!actions?.canBucket ? (
          <DropdownMenuItem disabled className="text-[12.5px]">
            No contact record yet
          </DropdownMenuItem>
        ) : actions.buckets.length ? (
          actions.buckets.slice(0, 8).map((bucket) => (
            <DropdownMenuItem
              key={bucket.id}
              disabled={actions.isBusy}
              onSelect={() => actions.putInBucket(bucket.id, bucket.label)}
              className="text-[12.5px]"
            >
              <span className="truncate">{bucket.label}</span>
            </DropdownMenuItem>
          ))
        ) : (
          <DropdownMenuItem disabled className="text-[12.5px]">
            No buckets yet
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

function Header({
  data,
  currentTab,
  onTabChange,
  onClose,
  onEdit,
  onMoveStage,
  onNewTask,
  actions,
}: {
  data: GraphiteCandidateProfileData
  currentTab: OverlayTab
  onTabChange: (tab: OverlayTab) => void
  onClose: () => void
  onEdit?: () => void
  onMoveStage?: () => void
  /** Straight to the composer — the count beside it is what opens the list. */
  onNewTask?: () => void
  actions?: CandidateActions
}) {
  // A record with no stage still needs an avatar tint; the token follows
  // the theme where the old literal teal did not.
  const stageColor = data.stageColor ?? stageHex("interviewing")

  return (
    // The record's header is chrome — the same white every page header in the
    // app carries. It had a tint of its own, which stacked a grey band directly
    // over the white counts strip beneath it and read as two surfaces where the
    // record has one. Hairlines do the dividing here, as they do in the body.
    <header className="shrink-0 bg-chrome">
      {/* Narrow: a 44px action row above the identity, per 8i. */}
      <div className="flex items-center justify-end px-2 pt-1 @2xl:hidden">
        <button
          type="button"
          onClick={onEdit}
          aria-label="Edit profile"
          className="flex h-11 w-11 items-center justify-center text-muted-foreground"
        >
          <SquarePen className="h-[17px] w-[17px]" />
        </button>
        <CandidateActionsMenu actions={actions} />
        <button
          type="button"
          onClick={onClose}
          aria-label="Close profile"
          className="flex h-11 w-11 items-center justify-center text-muted-foreground"
        >
          <X className="h-[18px] w-[18px]" />
        </button>
      </div>

      <div className="px-4 pt-1 @2xl:px-[26px] @2xl:pt-5">
        <div className="flex flex-wrap items-start gap-3 @2xl:gap-3.5">
          <span
            className="flex h-11 w-11 shrink-0 items-center justify-center overflow-hidden rounded-full text-base font-medium @2xl:h-[42px] @2xl:w-[42px] @2xl:text-sm"
            style={{
              backgroundColor: stageTint(stageColor, TINT.pill),
              color: stageColor,
            }}
          >
            {data.avatarUrl ? (
              <img src={data.avatarUrl} alt="" className="h-full w-full object-cover" />
            ) : (
              data.initials
            )}
          </span>

          <div className="min-w-0">
            <h1 className="truncate text-[22px] font-semibold leading-[1.15] tracking-[-0.025em] @2xl:text-[21px]">
              {data.name}
            </h1>
            <p className="mt-[5px] truncate text-xs leading-[1.4] text-muted-foreground @2xl:mt-1.5 @2xl:text-[12.5px]">
              {data.subtitle}
            </p>
          </div>

          {/* Wide: 14a's row of words — Add to Job, Add a task, Move Stage,
              Edit — then the close. "Add to Job" is the menu, because a job has
              to be picked and the artboard has nowhere to draw the picking. */}
          <div className="ml-auto hidden shrink-0 items-center gap-[18px] @2xl:flex">
            <CandidateActionsMenu actions={actions} label="Add to Job" />
            <button type="button" onClick={onNewTask} className={HEADER_ACTION}>
              Add a task
            </button>
            <button type="button" onClick={onMoveStage} className={HEADER_ACTION}>
              Move Stage
            </button>
            <button type="button" onClick={onEdit} className={HEADER_ACTION}>
              Edit
            </button>
            <button
              type="button"
              onClick={onClose}
              aria-label="Close profile"
              className="flex h-7 w-7 items-center justify-center rounded-full border text-muted-foreground transition-colors hover:text-foreground"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* Narrow: stage and the job it belongs to, per 8i. */}
        {(data.stage || data.jobTitle) && (
          <div className="mt-3 flex items-center gap-2.5 pb-1 @2xl:hidden">
            {data.stage && (
              <button
                type="button"
                onClick={onMoveStage}
                /* The pill is the narrow layout's only way into the stage
                   ladder, and read on its own "interviewing" does not say it
                   does anything. */
                aria-label={`${data.stage} — move stage`}
                className="rounded-full transition-opacity hover:opacity-80"
              >
                <TonePill hex={stageColor} compact>
                  {data.stage}
                </TonePill>
              </button>
            )}
            {data.jobTitle && (
              <span className="min-w-0 truncate text-[11.5px] leading-[1.3] text-muted-foreground">
                {data.jobTitle}
              </span>
            )}
          </div>
        )}
      </div>

      <div className="relative mt-3 border-b @2xl:mt-4">
        <div
          role="tablist"
          aria-label="Candidate views"
          className="flex gap-3.5 overflow-x-auto px-4 pr-7 @2xl:gap-5 @2xl:px-[26px]"
        >
          {TABS.map((tab) => (
            <button
              key={tab.id}
              type="button"
              role="tab"
              aria-selected={currentTab === tab.id}
              onClick={() => onTabChange(tab.id)}
              className={cn(
                "-mb-px whitespace-nowrap border-b-2 py-3 text-[13px] transition-colors @2xl:px-0.5 @2xl:py-2.5 @2xl:text-xs",
                currentTab === tab.id
                  ? "border-primary font-medium text-foreground"
                  : "border-transparent text-muted-foreground hover:text-foreground",
              )}
            >
              {tab.label}
            </button>
          ))}
        </div>
        <span className="pointer-events-none absolute inset-y-0 right-0 w-7 bg-gradient-to-r from-transparent to-chrome @2xl:hidden" />
      </div>
    </header>
  )
}

/**
 * The counts, as one strip under the tabs — at every width, and on every tab.
 *
 * This is the record's only always-present route to comments. It used to be
 * narrow-only, with the rail covering wide, which left a band between 672 and
 * 896 where neither appeared, and made comments unreachable from any tab but
 * Profile at any width.
 */
function CountsStrip({
  data,
  onOpenComments,
  onNewComment,
  onOpenTasks,
  onNewTask,
}: {
  data: GraphiteCandidateProfileData
  onOpenComments?: () => void
  onNewComment?: () => void
  onOpenTasks?: () => void
  /** Straight to the composer — the count beside it is what opens the list. */
  onNewTask?: () => void
}) {
  const counts = [
    { label: "Files", value: data.fileCount },
    { label: "Buckets", value: data.bucketCount },
  ].filter((count): count is { label: string; value: number } => count.value !== undefined)
  return (
    <div className="flex shrink-0 items-center gap-4 border-b bg-card px-4 py-2.5 @2xl:px-[26px]">
      {/*
        Comments and Tasks are the counts with somewhere to go — the others have
        no data source yet, so they read as figures rather than as controls.

        Turn 20: "Counts are the trigger. A row with a number opens the list; the
        action beside it opens the composer." The count opened the composer, so
        there was no way to simply read the record — reading meant writing first.
      */}
      <span className="inline-flex items-baseline gap-2 whitespace-nowrap">
        <button
          type="button"
          onClick={onOpenComments}
          className="inline-flex items-center gap-1.5 rounded text-[11px] text-muted-foreground underline-offset-4 transition-colors hover:text-foreground hover:underline"
        >
          Comments
          <span className="font-mono text-[11px] font-medium text-foreground">
            {data.commentCount ?? "—"}
          </span>
        </button>
        <button
          type="button"
          onClick={onNewComment}
          className="rounded text-[11px] text-brand-ink underline-offset-4 transition-opacity hover:opacity-80"
        >
          New
        </button>
      </span>
      <span className="inline-flex items-baseline gap-2 whitespace-nowrap">
        <button
          type="button"
          onClick={onOpenTasks}
          className="inline-flex items-center gap-1.5 whitespace-nowrap rounded text-[11px] text-muted-foreground underline-offset-4 transition-colors hover:text-foreground hover:underline"
        >
          Tasks
          <span className="font-mono text-[11px] font-medium text-foreground">
            {data.taskCount ?? "—"}
          </span>
        </button>
        <button
          type="button"
          onClick={onNewTask}
          className="rounded text-[11px] text-brand-ink underline-offset-4 transition-opacity hover:opacity-80"
        >
          New
        </button>
      </span>
      {counts.map((count) => (
        <span
          key={count.label}
          className="inline-flex items-center gap-1.5 whitespace-nowrap text-[11px] text-muted-foreground"
        >
          {count.label}
          <span className="font-mono text-[11px] font-medium text-foreground">
            {count.value}
          </span>
        </span>
      ))}
    </div>
  )
}

function ProfileBody({ data }: { data: GraphiteCandidateProfileData }) {
  /*
    The record carries the first few roles and says how many more there are.
    That line was a button with no handler, so the rest were unreachable.
  */
  const [showAllExperience, setShowAllExperience] = useState(false)
  const shownExperiences = showAllExperience ? data.allExperiences : data.experiences
  const hidden = showAllExperience
    ? 0
    : data.totalExperiences - data.experiences.length
  const stats = useMemo(
    () => [
      { value: data.location, label: "location" },
      { value: data.experienceYears, label: "exp" },
      { value: data.salary, label: "salary", accent: true },
      { value: data.bonus, label: "bonus", wideOnly: true },
    ],
    [data],
  )

  return (
    <div className="flex-1 rounded-[10px] border bg-card px-4 py-4 @2xl:px-6 @2xl:py-5">
      {data.summary && (
        <p className="text-pretty text-[13px] leading-[1.75] @2xl:text-sm">
          {data.summary}
        </p>
      )}

      {/*
        The numbers that decide a call. Salary is teal because it usually is the
        one. Bonus drops when narrow — 8i carries three.
      */}
      <div className="my-5 flex gap-4 border-y py-4 @2xl:my-[22px] @2xl:gap-6 @2xl:py-[18px]">
        {stats.map((stat) => (
          <div
            key={stat.label}
            className={cn("min-w-0 flex-1", stat.wideOnly && "hidden @2xl:block")}
          >
            <div
              className={cn(
                "truncate font-mono text-sm font-medium",
                stat.accent && "text-brand-ink",
              )}
            >
              {stat.value || "—"}
            </div>
            <div className="mt-[7px] text-[10.5px] text-muted-foreground">
              {stat.label}
            </div>
          </div>
        ))}
      </div>

      <div className={cn(RAIL_LABEL, "mb-3")}>Experience</div>
      {shownExperiences.map((experience, index) => (
        <div
          key={`${experience.company}-${index}`}
          className="border-t py-4 @2xl:flex @2xl:gap-5"
        >
          {/* Wide: dates are a mono gutter, the spine of the list.
              Narrow: they sit under the employer, per 8i. */}
          <div className="hidden w-[88px] shrink-0 font-mono text-[11px] leading-[1.5] text-muted-foreground @2xl:block">
            {experience.starts ?? "—"} — {experience.ends ?? "now"}
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-[13.5px] font-medium leading-[1.3]">
              {experience.title}
            </div>
            <div className="mt-1 text-[11.5px] leading-[1.3] text-muted-foreground">
              {experience.company}
            </div>
            <div className="mt-1 font-mono text-[11px] text-muted-foreground @2xl:hidden">
              {experience.starts ?? "—"} — {experience.ends ?? "now"}
            </div>
            {experience.description && (
              <p className="mt-2 text-pretty text-[12.5px] leading-[1.65]">
                {experience.description}
              </p>
            )}
          </div>
        </div>
      ))}

      {hidden > 0 && (
        <button
          type="button"
          onClick={() => setShowAllExperience(true)}
          className="w-full border-t py-3 text-left text-xs text-brand-ink transition-opacity hover:opacity-80"
        >
          View {hidden} more position{hidden === 1 ? "" : "s"}
        </button>
      )}

      {data.skills.length > 0 && (
        <div className="mt-4 flex flex-wrap gap-1.5">
          {data.skills.slice(0, 8).map((skill) => (
            <span
              key={skill}
              className="whitespace-nowrap rounded-full border px-[9px] py-[5px] text-[11.5px] text-muted-foreground"
            >
              {skill}
            </span>
          ))}
          {data.skills.length > 8 && (
            <span className="whitespace-nowrap rounded-full border px-[9px] py-[5px] text-[11.5px] text-muted-foreground">
              +{data.skills.length - 8} more
            </span>
          )}
        </div>
      )}

      {/*
        Tags are not skills: skills come off the profile, tags are what your
        team decided about this person. The record showed both before the
        rebuild, and Edit profile has been writing tags all along with nowhere
        to read them back.
      */}
      {data.tags.length > 0 && (
        <div className="mt-5">
          <div className={cn(RAIL_LABEL, "mb-2.5")}>Tags</div>
          <div className="flex flex-wrap gap-x-3 gap-y-1.5">
            {data.tags.map((tag) => (
              <span key={tag} className="text-[12px] text-brand-ink">
                {tag}
              </span>
            ))}
          </div>
        </div>
      )}

      {data.education.length > 0 && (
        <div className="mt-5">
          <div className={cn(RAIL_LABEL, "mb-3")}>Education</div>
          {data.education.map((school, index) => (
            <div
              key={`${school.school}-${index}`}
              className="border-t py-3 @2xl:flex @2xl:gap-5"
            >
              <div className="hidden w-[88px] shrink-0 font-mono text-[11px] leading-[1.5] text-muted-foreground @2xl:block">
                {school.years ?? "—"}
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-[13px] font-medium leading-[1.3]">{school.school}</div>
                {school.detail && (
                  <div className="mt-1 text-[11.5px] leading-[1.3] text-muted-foreground">
                    {school.detail}
                  </div>
                )}
                {school.years && (
                  <div className="mt-1 font-mono text-[11px] text-muted-foreground @2xl:hidden">
                    {school.years}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {data.linkedinUrl && (
        <a
          href={data.linkedinUrl}
          target="_blank"
          rel="noreferrer noopener"
          className="mt-5 inline-flex items-center gap-[7px] text-[12.5px] font-medium text-brand-ink transition-opacity hover:opacity-80"
        >
          <ExternalLink className="h-3.5 w-3.5" />
          LinkedIn profile
        </a>
      )}
    </div>
  )
}

/**
 * Working state — tasks, comments, and the counts with nothing behind them yet.
 *
 * 6a draws this as a rail beside the record, which only fits from @4xl. Below
 * that it renders inline under the profile body instead of disappearing: the
 * rail used to be the *only* route to comments and tasks, so between 672 and
 * 896 there was no way to reach either, and above 896 they were still reachable
 * only from the Profile tab.
 */
function WorkingSections({
  data,
  onOpenComment,
  onOpenComments,
  onNewComment,
  onOpenTasks,
  onNewTask,
}: {
  data: GraphiteCandidateProfileData
  onOpenComment?: (commentId: string) => void
  onOpenComments?: () => void
  onOpenTasks?: () => void
  /** Straight to the composer — the count beside it is what opens the list. */
  onNewTask?: () => void
  onNewComment?: () => void
}) {
  // Attachments and Buckets have no data source yet. They stay as rows so the
  // rail keeps 6a's shape, but without a chevron or a hover state, because a
  // control that cannot go anywhere is worse than a figure that does not claim
  // it can.
  const counts = [
    { label: "Attachments", value: data.fileCount },
    { label: "Buckets", value: data.bucketCount },
  ].filter((count): count is { label: string; value: number } => count.value !== undefined)

  return (
    <div className="flex min-h-full flex-col gap-4 rounded-[10px] border bg-card p-[18px]">
        <div>
          <button
            type="button"
            onClick={onOpenTasks}
            className={cn(RAIL_LABEL, "mb-2 block transition-colors hover:text-foreground")}
          >
            Tasks · {data.taskCount ?? "—"}
          </button>
          {data.tasks.map((task) => (
            <div key={task.id} className="border-b py-2.5">
              <div className="flex items-baseline gap-2">
                <span
                  className={cn(
                    "mt-[5px] h-[5px] w-[5px] shrink-0 rounded-full",
                    task.overdue ? "bg-destructive" : "bg-primary",
                  )}
                />
                <span className="min-w-0 flex-1 text-xs font-medium leading-[1.4]">
                  {task.title}
                </span>
              </div>
              <div className="mt-1.5 flex gap-2 pl-[13px]">
                {task.due && (
                  <span className="whitespace-nowrap font-mono text-[10px] text-muted-foreground">
                    {task.due}
                  </span>
                )}
                {task.status && (
                  <span className="whitespace-nowrap font-mono text-[10px] text-muted-foreground">
                    {task.status}
                  </span>
                )}
              </div>
            </div>
          ))}
          {data.taskCount === 0 && (
            <p className="pb-1 text-[11.5px] text-muted-foreground">
              Nothing open.
            </p>
          )}
          {/*
            Same shape as comments below: the overflow used to hang off "Add a
            task", which pointed the one control meaning "there is more to see"
            at the composer. Seeing the rest and writing another are two
            different intentions and get two controls.
          */}
          <div className="mt-2.5 flex items-baseline gap-4">
            <button
              type="button"
              onClick={onNewTask}
              className="text-[11.5px] text-brand-ink transition-opacity hover:opacity-80"
            >
              Add a task
            </button>
            {(data.taskCount ?? 0) > data.tasks.length && (
              <button
                type="button"
                onClick={onOpenTasks}
                className="text-[11.5px] text-muted-foreground transition-colors hover:text-foreground"
              >
                See all {data.taskCount ?? ""}
              </button>
            )}
          </div>
        </div>

        <div className="border-t pt-3.5">
          <button
            type="button"
            onClick={onOpenComments}
            className={cn(RAIL_LABEL, "mb-2 block transition-colors hover:text-foreground")}
          >
            Comments · {data.commentCount ?? "—"}
          </button>
          {data.comments.map((comment) => (
            <button
              key={comment.id}
              type="button"
              onClick={() => onOpenComment?.(comment.id)}
              className="flex w-full items-start gap-2.5 border-b py-2.5 text-left transition-colors hover:text-foreground"
            >
              <span className="min-w-0 flex-1">
                <span className="block truncate text-xs leading-[1.4]">
                  {comment.preview}
                </span>
                <span className="mt-1.5 flex gap-2">
                  {comment.author && (
                    <span className="whitespace-nowrap text-[10px] text-muted-foreground">
                      {comment.author}
                    </span>
                  )}
                  {comment.when && (
                    <span className="whitespace-nowrap font-mono text-[10px] text-muted-foreground">
                      {comment.when}
                    </span>
                  )}
                </span>
              </span>
              <ChevronRight className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
            </button>
          ))}
          {data.commentCount === 0 && (
            <p className="pb-1 text-[11.5px] text-muted-foreground">
              No comments yet.
            </p>
          )}
          {/*
            The overflow used to hang off "Add a comment", which pointed the one
            control that means "there is more to read" at the composer. It has
            its own control now, beside the one that writes.
          */}
          <div className="mt-2.5 flex items-baseline gap-4">
            <button
              type="button"
              onClick={onNewComment}
              className="text-[11.5px] text-brand-ink transition-opacity hover:opacity-80"
            >
              Add a comment
            </button>
            {(data.commentCount ?? 0) > data.comments.length && (
              <button
                type="button"
                onClick={onOpenComments}
                className="text-[11.5px] text-muted-foreground transition-colors hover:text-foreground"
              >
                See all {data.commentCount ?? ""}
              </button>
            )}
          </div>
        </div>

        <div className="border-t pt-3.5">
          {counts.map((count) => (
            <div
              key={count.label}
              className="flex w-full items-center gap-2.5 border-b py-2.5 text-muted-foreground"
            >
              <span className="text-xs">{count.label}</span>
              <span className="ml-auto whitespace-nowrap font-mono text-[11.5px]">
                {count.value}
              </span>
            </div>
          ))}
        </div>
    </div>
  )
}

export function GraphiteCandidateProfile({
  data,
  currentTab,
  onTabChange,
  onClose,
  onEdit,
  onOpenComment,
  onOpenComments,
  onNewComment,
  onOpenTasks,
  onNewTask,
  onMoveStage,
  actions,
  takeover,
  children,
}: {
  data: GraphiteCandidateProfileData
  currentTab: OverlayTab
  onTabChange: (tab: OverlayTab) => void
  onClose: () => void
  onEdit?: () => void
  onOpenComment?: (commentId: string) => void
  /** The whole list — what the Comments count opens. */
  onOpenComments?: () => void
  onNewComment?: () => void
  onOpenTasks?: () => void
  /** Straight to the composer — the count beside it is what opens the list. */
  onNewTask?: () => void
  onMoveStage?: () => void
  /** What the record's overflow menu can do. */
  actions?: CandidateActions
  /** Rendered instead of the profile body when a non-default tab is open. */
  /**
   * The children have taken the whole record over, not just its body. Canvas
   * 14o gives a comment the entire phone screen — its own nav bar and nothing
   * else — so on narrow the identity header and counts strip step aside. Wide
   * keeps them, because 14a still draws them above the comment.
   */
  takeover?: boolean
  children?: React.ReactNode
}) {
  return (
    <div className="@container flex h-full min-h-0 flex-col bg-background">
      <div className={cn(takeover && "hidden @3xl:contents")}>
        <Header
          data={data}
          currentTab={currentTab}
          onTabChange={onTabChange}
          onClose={onClose}
          onEdit={onEdit}
          onMoveStage={onMoveStage}
          onNewTask={onNewTask}
          actions={actions}
        />

        <CountsStrip
          data={data}
          onOpenComments={onOpenComments}
          onNewComment={onNewComment}
          onOpenTasks={onOpenTasks}
          onNewTask={onNewTask}
        />
      </div>

      {/*
        A takeover is a document, and 14h, 14i, 14j and 14l all draw it as the
        white sheet body under the grey header — no padding, no card, hairlines
        doing the dividing. The Profile tab is the other kind of body: cards on
        the page's own ground, which is what makes a card read as an object.
      */}
      <div className={cn("flex min-h-0 flex-1", takeover && "bg-card")}>
        {/* Children win when there are any: a tab view, or the comment view,
            which is not a tab at all and so cannot be keyed off `currentTab`. */}
        <div className={cn("min-w-0 flex-1 overflow-y-auto", !takeover && "p-3.5")}>
          {children ?? (
            <div className="flex min-h-full flex-col gap-3.5">
              <ProfileBody data={data} />
              {/* Below @4xl the rail has no room, so the same sections run
                  under the record rather than disappearing entirely. */}
              <div className="@4xl:hidden">
                <WorkingSections
                  data={data}
                  onOpenComment={onOpenComment}
                  onOpenComments={onOpenComments}
                  onNewComment={onNewComment}
                  onOpenTasks={onOpenTasks}
                  onNewTask={onNewTask}
                />
              </div>
            </div>
          )}
        </div>
        {!children && (
          <div className="hidden w-[314px] shrink-0 overflow-y-auto py-3.5 pr-3.5 @4xl:block">
            <WorkingSections
              data={data}
              onOpenComment={onOpenComment}
              onOpenComments={onOpenComments}
              onNewComment={onNewComment}
              onOpenTasks={onOpenTasks}
              onNewTask={onNewTask}
            />
          </div>
        )}
      </div>
    </div>
  )
}
